import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import { SCUIButton } from '../../../../atomic/atoms/SCUIButton/SCUIButton';

const MIN_LENGTH = 10;
const MAX_LENGTH = 500;

interface RejectionReasonModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    isBulk?: boolean;
    selectedCount?: number;
}

export const RejectionReasonModal: React.FC<RejectionReasonModalProps> = ({
    open,
    onClose,
    onConfirm,
    isBulk = false,
    selectedCount = 0,
}) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        if (!open) {
            setReason('');
            setTouched(false);
        }
    }, [open]);

    const trimmedLength = reason.trim().length;
    const hasError = touched && (trimmedLength === 0 || trimmedLength < MIN_LENGTH);
    const isOverLimit = reason.length > MAX_LENGTH;

    const getHelperText = () => {
        if (!touched) return ' ';
        if (trimmedLength === 0) return t('roleApproval.rejection.rejectionReasonRequired');
        if (trimmedLength < MIN_LENGTH) return t('roleApproval.rejection.rejectionReasonMinLength');
        if (isOverLimit) return t('roleApproval.rejection.rejectionReasonMaxLength');
        return ' ';
    };

    const isValid = trimmedLength >= MIN_LENGTH && !isOverLimit;

    const handleConfirm = () => {
        setTouched(true);
        if (isValid) {
            onConfirm(reason.trim());
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #E2E8F0',
                    px: 4,
                    py: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #ef5350 0%, #c62828 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <BlockIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1E293B' }}>
                            {t('roleApproval.rejection.rejectionReasonTitle')}
                        </Typography>
                        {isBulk && selectedCount > 0 && (
                            <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                                {t('roleApproval.rejection.bulkRejectInfo', { count: selectedCount })}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: '#64748B' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 4, pt: 0, pb: 4 }}>
                <Typography variant="body1" sx={{ color: '#475569', mt: 4, mb: 3 }}>
                    {t('roleApproval.rejection.rejectionReasonDescription')}
                </Typography>
                <TextField
                    multiline
                    minRows={5}
                    maxRows={10}
                    fullWidth
                    value={reason}
                    onChange={(e) => {
                        if (e.target.value.length <= MAX_LENGTH) {
                            setReason(e.target.value);
                        }
                    }}
                    onBlur={() => setTouched(true)}
                    placeholder={t('roleApproval.rejection.rejectionReasonPlaceholder')}
                    error={hasError || isOverLimit}
                    helperText={getHelperText()}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            fontSize: '1rem',
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '14px 16px',
                        },
                    }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: isOverLimit
                                ? 'error.main'
                                : trimmedLength >= MIN_LENGTH
                                  ? 'success.main'
                                  : '#94A3B8',
                            fontWeight: 500,
                        }}
                    >
                        {reason.length}/{MAX_LENGTH}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: '1px solid #E2E8F0',
                    px: 4,
                    py: 3,
                    gap: 2,
                }}
            >
                <SCUIButton
                    onClick={onClose}
                    type="secondary"
                    buttonType="button"
                    sizeType="fixed"
                    size="l"
                    text={t('general.cancel')}
                />
                <SCUIButton
                    onClick={handleConfirm}
                    type="error"
                    buttonType="button"
                    sizeType="fixed"
                    size="l"
                    text={t('roleApproval.rejection.confirmRejectWithReason')}
                    disabled={touched && !isValid}
                    style={{
                        borderRadius: '8px',
                        fontWeight: 600,
                    }}
                />
            </DialogActions>
        </Dialog>
    );
};
